import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { buildColumnDefs, cellClassName } from "@platform/design-system";

import { formatDate, formatRevisedOrOriginalDate, isUTC } from "../../utils";
import {
  AnalystCellRender,
  ClientInfoCell,
  CreatorCellRender,
  ExpandIconCell,
  NonAdminDiscardCell,
  StatusCell,
} from "../../components/RfpsTable/ColumnCellRender";
import { ActionsCell } from "../../components/RfpsTable/ActionsCell";
import { RFP_STATUSES } from "../../constants/rfp";
import { RfpListItem } from "../../hooks/useRfpsList";
import { GridContext } from "../../types/rfpGridContext";
import { Rfp } from "../../hooks/useRfps";

// Sort mapping types
type SortKeyMapping = Record<string, string>;

const effectiveDateLabel = "Effective Date";
const clientNameLabel = "Client Name";
const rfpStatusId = "rfp.status";
const clientNameKey = "client.name";
const detailsDueDate = "details.dueDate";
const detailsEffectiveDate = "details.effectiveDate";

const COLUMN_HEADERS = [
  "id",
  clientNameKey,
  detailsDueDate,
  detailsEffectiveDate,
  "submitter",
  "assignedAnalyst",
  rfpStatusId,
  "actions",
] as const;

const ADMIN_COLUMN_HEADERS = [
  "id",
  clientNameKey,
  "brokerName",
  detailsDueDate,
  detailsEffectiveDate,
  "submitter",
  "assignedAnalyst",
  rfpStatusId,
  "actions",
] as const;

const DRAFT_COLUMN_HEADERS = [
  "id",
  "clientName",
  "dueDate",
  "currentStep",
  "creator",
  "lastModifyDate",
] as const;

const ADMIN_DRAFT_COLUMN_HEADERS = [
  "id",
  "clientName",
  "broker",
  "dueDate",
  "currentStep",
  "creator",
  "lastModifyDate",
] as const;

// Main headers
const ALL_HEADERS: Record<string, ColDef<RfpListItem>> = {
  id: {
    field: "id",
    headerName: "RFP ID",
    sortable: true,
    cellRenderer: ExpandIconCell,
  },
  [clientNameKey]: {
    field: clientNameKey,
    headerName: clientNameLabel,
    cellRenderer: ClientInfoCell,
    tooltipValueGetter: (params) =>
      [...new Set(params.data?.products ?? [])].toString(),
    sortable: false,
  },
  [detailsDueDate]: {
    field: detailsDueDate,
    headerName: "Due Date",
    cellDataType: "dateString",
    sortable: true,
    valueFormatter: ({ value, data }) =>
      formatRevisedOrOriginalDate(
        data?.revisedDueDate as string | null,
        value as string | null,
      ) as string,
  },
  [detailsEffectiveDate]: {
    field: detailsEffectiveDate,
    headerName: effectiveDateLabel,
    sortable: true,
    cellDataType: "dateString",
    valueFormatter: ({ value, data }) =>
      formatRevisedOrOriginalDate(
        data?.revisedEffectiveDate as string | null,
        value as string | null,
      ) as string,
  },
  submitter: {
    field: "submitter.displayName",
    headerName: "Submitted by",
    context: { onNull: "N/A" },
    cellRenderer: CreatorCellRender,
    valueGetter: (params) => {
      if (params.data?.submitter?.givenName) {
        return `${params.data?.submitter.givenName} ${params.data.submitter.surname || ""}`;
      }
      return (params.data?.submitter?.displayName ?? "N/A") as string;
    },
    sortable: true,
  },
  assignedAnalyst: {
    field: "assignedAnalyst.name",
    headerName: "Assigned Analyst",
    context: { onNull: "Pending Assignment" },
    valueGetter: (params) =>
      (params.data?.assignedAnalyst?.name ?? "Pending") as string,
    cellRenderer: AnalystCellRender,
    sortable: false,
  },
  [rfpStatusId]: {
    field: "details.status",
    headerName: "Status",
    sortable: false,
    valueFormatter: ({ value }) => (RFP_STATUSES[value] ?? "TBD") as string,
    cellRenderer: StatusCell,
    cellClass: "rfp-status-cell",
  },
  brokerName: {
    field: "brokerName",
    headerName: "Registered Broker",
    sortable: false,
  },
  actions: {
    colId: "actions",
    headerName: "",
    sortable: false,
    filter: false,
    cellRenderer: ActionsCell,
    width: 70,
    maxWidth: 70,
  },
};

export const ANALYST_HEADER: ColDef<Rfp>[] = [
  {
    field: "id",
    headerName: "RFP ID",
    sortable: true,
    cellStyle: { width: "100px" },
  },
  {
    field: clientNameKey,
    headerName: clientNameLabel,
    wrapText: true,
    sortable: true,
    autoHeight: true,
    cellStyle: {
      fontWeight: 600,
    },
    cellClass: cellClassName,
    valueGetter: (params) =>
      params.data?.client?.name ?? params.data?.details?.clientInfo?.name,
  },
  {
    field: "broker.name",
    headerName: "Broker",
    sortable: true,
  },
  {
    field: detailsEffectiveDate,
    headerName: effectiveDateLabel,
    cellDataType: "dateString",
    sortable: true,
    valueFormatter: ({ value }) => formatDate(value, isUTC(value)),
  },
  {
    field: "carrierDueDate",
    headerName: "Carrier Due Date",
    cellDataType: "dateString",
    sortable: true,
    valueFormatter: ({ value }) => formatDate(value, isUTC(value)) || "-",
  },
  {
    field: detailsDueDate,
    headerName: "Broker Due Date",
    cellDataType: "dateString",
    sortable: true,
    valueFormatter: ({
      data,
      value,
    }: ValueFormatterParams<Rfp, string>): string => {
      const revisedDueDate = data?.revisedDueDate;
      const dateToFormat = revisedDueDate ?? value;

      return formatDate(dateToFormat, isUTC(dateToFormat));
    },
  },
  {
    field: "details.status",
    headerName: "Status",
    valueFormatter: ({ value }) =>
      (value === null ? "Pending Assignment" : value) as string,
    sortable: true,
    cellRenderer: StatusCell,
  },
];

// Sort key mappings
export const analystSortKeyMapping: SortKeyMapping = {
  id: "id",
  "broker.name": "brokerName",
  "details.status": "status",
  [clientNameKey]: "clientName",
  [detailsEffectiveDate]: "effectiveDate",
  carrierDueDate: "carrierDueDate",
  [detailsDueDate]: "dueDate",
};

export const rfpSortKeysMapping: SortKeyMapping = {
  id: "id",
  status: "status",
  clientName: "clientName",
  [clientNameKey]: "clientName",
  currentStep: "currentStep",
  creator: "submittedBy",
  [detailsDueDate]: "dueDate",
  [detailsEffectiveDate]: "effectiveDate",
  "submitter.displayName": "submittedBy",
  "assignedAnalyst.name": "analystName",
  lastModifyDate: "lastModifyDate",
};

export const buildRfpSortKey = (
  sortBy: string | undefined,
  isDescending: boolean,
): string => {
  if (!sortBy) return "";
  const apiKey = rfpSortKeysMapping[sortBy];
  if (!apiKey) return "";
  return `${isDescending ? "-" : ""}${apiKey}`;
};

const DRAFT_COLUMNS: Record<string, ColDef<RfpListItem>> = {
  id: {
    field: "id",
    headerName: "RFP ID",
    sortable: false,
    flex: 0.3,
  },
  clientName: {
    field: clientNameKey,
    headerName: clientNameLabel,
    sortable: true,
    flex: 0.5,
    valueGetter: (params) => {
      const item = params.data;
      const clientObj = item?.client as { name?: string } | undefined;
      return (clientObj?.name ?? item?.clientName) as string | null | undefined;
    },
  },
  dueDate: {
    field: "dueDate",
    headerName: "Due Date",
    sortable: false,
    flex: 0.5,
  },
  currentStep: {
    field: "currentStep",
    headerName: "Current Step",
    sortable: true,
    flex: 0.5,
  },
  creator: {
    field: "creator",
    headerName: "Draft by",
    context: { onNull: "N/A" },
    valueGetter: (params) =>
      params.data?.creator?.givenName
        ? `${params.data.creator.givenName} ${params.data.creator.surname || ""}`
        : "N/A",
    cellRenderer: CreatorCellRender,
    autoHeight: true,
    sortable: true,
    flex: 1,
  },
  lastModifyDate: {
    field: "lastModifyDate",
    headerName: "Last updated",
    cellDataType: "dateString",
    valueFormatter: (params) =>
      (params.value ? formatDate(params.value) : "—") as string,

    cellRenderer: NonAdminDiscardCell,
    cellRendererParams: (params: { context: GridContext }) => ({
      openDiscardDialog: params.context?.openDiscardDialog,
    }),
    sortable: true,
    flex: 1,
  },
  broker: {
    field: "brokerName",
    headerName: "Registered Broker",
    sortable: false,
    flex: 0.5,
  },
};

export const headers = buildColumnDefs<
  RfpListItem,
  typeof ALL_HEADERS,
  typeof COLUMN_HEADERS
>(ALL_HEADERS, COLUMN_HEADERS, {
  cellStyle: {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
});
export const headersAdmin = buildColumnDefs<
  RfpListItem,
  typeof ALL_HEADERS,
  typeof ADMIN_COLUMN_HEADERS
>(ALL_HEADERS, ADMIN_COLUMN_HEADERS, {
  cellStyle: {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
});
export const draftHeaders = buildColumnDefs<
  RfpListItem,
  typeof DRAFT_COLUMNS,
  typeof DRAFT_COLUMN_HEADERS
>(DRAFT_COLUMNS, DRAFT_COLUMN_HEADERS, {
  cellStyle: {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
});
export const adminDraftHeaders = buildColumnDefs<
  RfpListItem,
  typeof DRAFT_COLUMNS,
  typeof ADMIN_DRAFT_COLUMN_HEADERS
>(DRAFT_COLUMNS, ADMIN_DRAFT_COLUMN_HEADERS, {
  cellStyle: {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
});
